import React, { useCallback } from 'react';

import { OrbitMutationFailure } from 'api/orbit';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { useOrbitContext } from './OrbitContext';
import { TOrbit } from './i18n/TModules';

/** Contract §9: the server rejects questions over 2000 characters after trim. */
export const ORBIT_QUESTION_MAX_LENGTH = 2000;

/**
 * The failures whose own copy beats anything the response carried: a network drop has no body at
 * all, a 5xx body is a gateway page rather than a sentence, and a 401's "invalid session" does not
 * tell the reader to sign in again. Every other kind keeps the backend message (PLA-9563 §8).
 */
const ASK_ERROR_CAUSE_KEY: Partial<Record<OrbitMutationFailure['kind'], string>> = {
  network: 'notice.askFailedNetwork',
  serverError: 'notice.askFailedServer',
  sessionExpired: 'notice.askFailedSession'
};

/**
 * The ask behaviour every Orbit composer shares (the panel's and the full page's): the
 * provider's draft, the length rule, submit-on-Enter and the failure copy. Only the look differs.
 */
const useOrbitComposer = () => {
  const { t } = useMultiLanguageTranslation();
  const { ask, isAsking, askError, draft, setDraft } = useOrbitContext();

  const length = draft.trim().length;
  const overLimit = length > ORBIT_QUESTION_MAX_LENGTH;
  const canSubmit = length > 0 && !overLimit && !isAsking;

  const submit = useCallback(async () => {
    if (!canSubmit) {
      return;
    }

    const outcome = await ask(draft);

    // A failed POST leaves the question in place so the user can edit or resend it; `askError`
    // says why.
    if (outcome.kind === 'ok') {
      setDraft('');
    }
  }, [ask, canSubmit, draft, setDraft]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    submit();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      submit();
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) =>
    setDraft(event.target.value);

  const askErrorText = (() => {
    if (askError === null) {
      return null;
    }

    const causeKey = ASK_ERROR_CAUSE_KEY[askError.kind];

    return causeKey === undefined
      ? askError.message ?? t(`${TOrbit}.notice.askFailed`)
      : t(`${TOrbit}.${causeKey}`);
  })();

  return {
    draft,
    isAsking,
    overLimit,
    canSubmit,
    submit,
    handleSubmit,
    handleKeyDown,
    handleChange,
    askErrorText
  };
};

export default useOrbitComposer;
